import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../components/Button'
import { useAuth } from '../../contexts/AuthContext'
import { getGoal } from '../../services/goals'
import {
  createGoalEvent,
  getGoalEvent,
  updateGoalEvent,
  type CreateGoalEventRequest,
  type GoalEvent,
} from '../../services/goalEvents'
import {
  getSignedUploadUrl,
  uploadToS3,
  getSignedDownloadUrl,
  deleteWorkSample,
} from '../../services/workSamples'
import { Goal, Student } from '../../types'

const objectiveStatusOptions = ['Met', 'Not Met', 'Not Introduced']

const goalStatusOptions = [
  'Goal Achieved',
  'Making Sufficient Progress',
  'Not Making Sufficient Progress',
  'Not Yet Introduced',
  'Not Applicable To My Subject Area',
]

const independenceOptions = [
  'Fully Independent',
  'Making Satisfactory Progress',
  'Highly Dependent Upon Adult Support',
  'Not Applicable To My Subject Area',
]

interface UploadedSampleItemProps {
  s3Key: string
  getDownloadUrl: (s3Key: string) => Promise<string>
  onDownload: (s3Key: string) => Promise<void>
  onDelete: (s3Key: string) => void
  disabled: boolean
}

const getFilenameFromS3Key = (s3Key: string): string => {
  const parts = s3Key.split('/')
  const filenamePart = parts[parts.length - 1]
  const underscoreIndex = filenamePart.indexOf('_')

  return underscoreIndex > 0
    ? filenamePart.substring(underscoreIndex + 1)
    : filenamePart
}

const UploadedSampleItem: React.FC<UploadedSampleItemProps> = ({
  s3Key,
  getDownloadUrl,
  onDownload,
  onDelete,
  disabled,
}) => {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(s3Key)

  const filename = getFilenameFromS3Key(s3Key)

  // Extract file extension
  const getFileExtension = (): string => {
    const lastDotIndex = filename.lastIndexOf('.')
    return lastDotIndex > 0 ? filename.substring(lastDotIndex) : 'file'
  }

  const fileExtension = getFileExtension()

  useEffect(() => {
    getDownloadUrl(s3Key)
      .then(setDownloadUrl)
      .catch((err) => {
        console.error('Failed to generate download URL:', err)
        setDownloadUrl(null)
      })
      .finally(() => setLoading(false))
  }, [s3Key, getDownloadUrl])

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-16 bg-gray-200 rounded border border-gray-300 animate-pulse" />
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    )
  }

  if (!downloadUrl) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-16 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
          <span className="text-xs text-gray-600">Error</span>
        </div>
        <span className="text-sm text-red-600">Failed to load sample</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {isImage ? (
        <img
          src={downloadUrl}
          alt={filename}
          className="w-16 h-16 object-cover rounded border border-gray-300"
        />
      ) : (
        <div className="w-16 h-16 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
          <span className="text-xs text-gray-600">{fileExtension}</span>
        </div>
      )}
      <span className="text-sm flex-1 truncate text-gray-800">{filename}</span>
      <Button
        type="button"
        onClick={() => onDownload(s3Key)}
        disabled={disabled}
        variant="secondary"
        className="px-3 py-1 text-sm border-blue-200 text-blue-700 hover:bg-blue-50"
      >
        Download
      </Button>
      <Button
        type="button"
        onClick={() => onDelete(s3Key)}
        disabled={disabled}
        variant="link"
        className="px-3 py-1 text-sm border border-red-200 text-red-700 hover:bg-red-50 rounded"
      >
        Delete
      </Button>
    </div>
  )
}

const GoalEventForm: React.FC = () => {
  const { goalId, goalEventId } = useParams<{
    goalId: string
    goalEventId?: string
  }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [goal, setGoal] = useState<Goal | null>(null)
  const [existingGoalEvent, setExistingGoalEvent] = useState<GoalEvent | null>(
    null,
  )
  const [period, setPeriod] = useState('')
  const [objectiveStatuses, setObjectiveStatuses] = useState<string[]>([])
  const [status, setStatus] = useState('')
  const [levelOfIndependence, setLevelOfIndependence] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadedSamples, setUploadedSamples] = useState<string[]>([])
  const [removedSamples, setRemovedSamples] = useState<string[]>([])
  const [signedDownloadUrls, setSignedDownloadUrls] = useState<
    Record<string, string>
  >({})
  const [workSampleActionLoading, setWorkSampleActionLoading] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const isEditing = Boolean(goalEventId)

  const goalObjectives = useMemo(() => goal?.objectives || [], [goal])

  useEffect(() => {
    if (goalId) {
      fetchData()
    }
  }, [goalId, goalEventId])

  const fetchData = async () => {
    if (!goalId) return

    setLoading(true)
    setError(null)

    try {
      const goalResponse = await getGoal(goalId)
      const loadedGoal = goalResponse.data
      setGoal(loadedGoal)

      const defaultStatuses = loadedGoal.objectives.map(() => 'Not Introduced')

      if (goalEventId) {
        const goalEventResponse = await getGoalEvent(goalEventId)
        const loadedEvent = goalEventResponse.data
        setExistingGoalEvent(loadedEvent)

        setPeriod(loadedEvent.period)
        setStatus(loadedEvent.status)
        setLevelOfIndependence(loadedEvent.levelOfIndependence)
        setNotes(loadedEvent.notes || '')
        setUploadedSamples(loadedEvent.workSamples || [])

        const mappedStatuses = loadedGoal.objectives.map((objective) => {
          const match = loadedEvent.objectivesWithStatus.find(
            (item) => item.objective === objective,
          )
          return match?.status || 'Not Introduced'
        })
        setObjectiveStatuses(mappedStatuses)
      } else {
        setObjectiveStatuses(defaultStatuses)
      }
    } catch (err: any) {
      console.error('Error fetching goal event form data:', err)
      setError(err.response?.data?.error || 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }

  const handleObjectiveStatusChange = (index: number, value: string) => {
    const next = [...objectiveStatuses]
    next[index] = value
    setObjectiveStatuses(next)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploadError(null)

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ]

    if (!allowedTypes.includes(file.type)) {
      setUploadError(
        'File type not allowed. Please upload documents or images.',
      )
      return
    }

    if (file.size > 10485760) {
      setUploadError('File size exceeds 10MB limit')
      return
    }

    setSelectedFile(file)
  }

  const handleUploadFile = async () => {
    if (!selectedFile || !goalId) return

    setUploadingFile(true)
    setUploadError(null)

    try {
      const { signedUrl, s3Key } = await getSignedUploadUrl({
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        goalEventId: goalId,
        fileSize: selectedFile.size,
      })

      await uploadToS3(signedUrl, selectedFile)

      // Store s3Key, not full URL
      setUploadedSamples([...uploadedSamples, s3Key])
      setSelectedFile(null)

      const input = document.getElementById(
        'work-sample-input',
      ) as HTMLInputElement
      if (input) input.value = ''
    } catch (err: any) {
      console.error('Error uploading file:', err)
      setUploadError(
        err.response?.data?.error || err.message || 'Failed to upload file',
      )
    } finally {
      setUploadingFile(false)
    }
  }

  const handleDownloadSample = async (s3Key: string) => {
    setWorkSampleActionLoading(true)
    setUploadError(null)

    try {
      const downloadUrl = await getSignedDownloadUrl(s3Key)
      const response = await fetch(downloadUrl)

      if (!response.ok) {
        throw new Error('Failed to download file')
      }

      const blob = await response.blob()
      const fileName = getFilenameFromS3Key(s3Key)
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (err: any) {
      console.error('Error downloading file:', err)
      setUploadError(err.message || 'Failed to download file')
    } finally {
      setWorkSampleActionLoading(false)
    }
  }

  const handleDeleteSample = (s3Key: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this work sample? This change will be applied when you save.',
    )

    if (!confirmed) {
      return
    }

    setUploadedSamples((previous) => previous.filter((item) => item !== s3Key))
    setRemovedSamples((previous) =>
      previous.includes(s3Key) ? previous : [...previous, s3Key],
    )
  }

  const getDownloadUrl = useCallback(
    async (s3Key: string): Promise<string> => {
      // Check if we already have a cached signed URL
      if (signedDownloadUrls[s3Key]) {
        return signedDownloadUrls[s3Key]
      }

      // Generate a new signed URL
      const url = await getSignedDownloadUrl(s3Key)
      setSignedDownloadUrls({ ...signedDownloadUrls, [s3Key]: url })
      return url
    },
    [signedDownloadUrls],
  )

  const getStudentIdForSubmit = (): string | null => {
    if (!goal) return null

    const student = goal.student
    if (typeof student === 'string') {
      return student
    }

    return (student as Student)._id
  }

  const getTeacherIdForSubmit = (): string | null => {
    if (existingGoalEvent) {
      return typeof existingGoalEvent.teacher === 'string'
        ? existingGoalEvent.teacher
        : existingGoalEvent.teacher._id
    }

    return user?.id || user?._id || null
  }

  const validate = (): string | null => {
    if (!period.trim()) return 'Time Period is required'
    if (!status) return 'Status is required'
    if (!levelOfIndependence) return 'Level of independence is required'
    if (goalObjectives.length === 0) return 'Goal has no objectives to log'

    return null
  }

  const handleSave = async () => {
    if (!goalId) return

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    const studentId = getStudentIdForSubmit()
    const teacherId = getTeacherIdForSubmit()

    if (!studentId) {
      setError('Could not determine student for this goal event')
      return
    }

    if (!teacherId) {
      setError('Could not determine teacher for this goal event')
      return
    }

    if (removedSamples.length > 0) {
      setSaving(true)
      setError(null)

      try {
        for (const s3Key of removedSamples) {
          await deleteWorkSample(s3Key)
        }
        setRemovedSamples([])
      } catch (err: any) {
        console.error('Error deleting work samples:', err)
        setUploadedSamples((previous) => [
          ...new Set([...previous, ...removedSamples]),
        ])
        setError(
          err.response?.data?.error ||
            err.message ||
            'Failed to delete one or more work samples',
        )
        setSaving(false)
        return
      }
    }

    const payload: CreateGoalEventRequest = {
      student: studentId,
      teacher: teacherId,
      goal: goalId,
      period: period.trim(),
      objectivesWithStatus: goalObjectives.map((objective, index) => ({
        objective,
        status: objectiveStatuses[index] || 'Not Introduced',
      })),
      status,
      levelOfIndependence,
      notes: notes.trim(),
      workSamples: uploadedSamples,
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditing && goalEventId) {
        await updateGoalEvent(goalEventId, payload)
      } else {
        await createGoalEvent(payload)
      }
      navigate(`/goals/${goalId}/goal-events`)
    } catch (err: any) {
      console.error('Error saving goal event:', err)
      setError(err.response?.data?.error || 'Failed to save goal event')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!goal) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        Goal not found.
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Button
          onClick={() => navigate(`/goals/${goalId}/goal-events`)}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          ← Back to Progress Reports
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {isEditing ? 'Edit Progress Report' : 'Create Progress Report'}
        </h1>
        <strong>Goal Description:</strong>
        <p className="mt-2 text-gray-600">{goal.description}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Time Period
          </h2>
          <input
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter period"
          />
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Objectives
          </h2>
          <div className="space-y-3">
            {goalObjectives.map((objective, index) => (
              <div
                key={`${objective}-${index}`}
                className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center"
              >
                <p className="text-sm text-gray-800">{objective}</p>
                <select
                  value={objectiveStatuses[index] || 'Not Introduced'}
                  onChange={(e) =>
                    handleObjectiveStatusChange(index, e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {objectiveStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Goal Status
          </h2>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select status</option>
            {goalStatusOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Level of Independence
          </h2>
          <select
            value={levelOfIndependence}
            onChange={(e) => setLevelOfIndependence(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select level of independence</option>
            {independenceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add notes"
          />
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Work Samples
          </h2>

          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
              <p className="text-red-800 text-sm">{uploadError}</p>
            </div>
          )}

          <div className="space-y-3">
            {uploadedSamples.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-2">
                  Uploaded Samples
                </h3>
                <div className="space-y-2">
                  {uploadedSamples.map((s3Key, index) => (
                    <UploadedSampleItem
                      key={`${s3Key}-${index}`}
                      s3Key={s3Key}
                      getDownloadUrl={getDownloadUrl}
                      onDownload={handleDownloadSample}
                      onDelete={handleDeleteSample}
                      disabled={
                        saving || uploadingFile || workSampleActionLoading
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                id="work-sample-input"
                type="file"
                onChange={handleFileSelect}
                disabled={uploadingFile}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                className="hidden"
              />
              <label htmlFor="work-sample-input" className="cursor-pointer">
                {selectedFile ? (
                  <>
                    <p className="text-sm text-gray-900">
                      <strong>{selectedFile.name}</strong>
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Click to select or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supported: Documents and Images (Max 10 MB)
                    </p>
                  </>
                )}
              </label>

              {selectedFile && (
                <Button
                  onClick={handleUploadFile}
                  disabled={uploadingFile}
                  variant="primary"
                  loading={uploadingFile}
                  className="mt-3"
                >
                  Upload File
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center gap-3 pt-2">
          <Button
            onClick={() => navigate(`/goals/${goalId}/goal-events`)}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="primary"
            loading={saving}
          >
            Save Progress Report
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GoalEventForm
